import { useState, useEffect } from "react";
import { PaymentSummary } from "../types/PaymentTypes";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const usePaymentMethods = (summaries: PaymentSummary[]) => {
  const { data: ownerData } = useQuery({
    queryKey: ["ownerData"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data, error } = await supabase
        .from("owner_data")
        .select(`
          *,
          business_types (
            id,
            name
          )
        `)
        .eq("owner_id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const isCommerceOrHardware = ownerData?.business_types?.name === 'commerce' || ownerData?.business_types?.name === 'quincaillerie';

  const paymentMethods = isCommerceOrHardware ? [
    "Espèces",
    "Mobile Money",
    "Virement bancaire",
    "Wave",
    "Orange Money",
    "Autres mode de paiement",
  ] : [
    "Espèces",
    "Mobile Money",
    "Virement bancaire",
    "Wave",
    "Orange Money",
    "Autres mode de paiement",
  ];

  const completeSummaries = paymentMethods.map((method) => {
    const existingSummary = summaries.find((s) => s.method === method);
    return existingSummary || { 
      method, 
      amount: 0,
      transactions: []
    };
  });

  const [visibleMethods, setVisibleMethods] = useState<Set<string>>(() => {
    const savedMethods = localStorage.getItem("visiblePaymentMethods");
    return savedMethods
      ? new Set(JSON.parse(savedMethods))
      : new Set(completeSummaries.map((s) => s.method));
  });

  const [expandedMethods, setExpandedMethods] = useState<Set<string>>(new Set());

  useEffect(() => {
    localStorage.setItem(
      "visiblePaymentMethods",
      JSON.stringify(Array.from(visibleMethods))
    );
  }, [visibleMethods]);

  const toggleMethodVisibility = (method: string, checked: boolean) => {
    const newVisibleMethods = new Set(visibleMethods);

    if (checked) {
      newVisibleMethods.add(method);
    } else {
      if (visibleMethods.size > 1) {
        newVisibleMethods.delete(method);
      }
    }

    setVisibleMethods(newVisibleMethods);
  };

  const toggleDetails = (method: string) => {
    const newExpandedMethods = new Set(expandedMethods);
    if (expandedMethods.has(method)) {
      newExpandedMethods.delete(method);
    } else {
      newExpandedMethods.add(method);
    }
    setExpandedMethods(newExpandedMethods);
  };

  return {
    completeSummaries,
    visibleMethods,
    expandedMethods,
    toggleMethodVisibility,
    toggleDetails,
  };
};